import { useState, useEffect, useMemo } from 'react';
import { useExchangeRates } from './useExchangeRates';
import { AccountColorManager } from '@/lib/accountColors';
import type { AccountWithConvertedBalance } from '@/types/account-drilldown';
import {Account} from "@/integrations/supabase/account.models.ts";

export type { AccountWithConvertedBalance };

const SESSION_STORAGE_KEY = 'selected-currency';

export interface UseCurrencyConversionOptions {
  baseCurrency: string;
  accounts: Account[];
}

export interface UseCurrencyConversionReturn {
  selectedCurrency: string;
  setSelectedCurrency: (currency: string) => void;
  convertedAccounts: AccountWithConvertedBalance[];
  totalNetWorth: number;
  isConverting: boolean;
  conversionError: Error | null;
  accountColors: Map<string, string>;
}

/**
 * Custom hook for managing currency conversion on the Account Overview page
 * 
 * Features:
 * - Manages selected currency state with sessionStorage persistence
 * - Converts all account balances to selected currency
 * - Calculates total net worth in selected currency
 * - Generates and maintains account color assignments
 * - Handles conversion errors with automatic fallback to base currency
 * - Provides loading states during conversion
 */
export function useCurrencyConversion(
  options: UseCurrencyConversionOptions
): UseCurrencyConversionReturn {
  const { baseCurrency, accounts } = options;

  // Initialize selected currency from session storage or default to base currency
  const [selectedCurrency, setSelectedCurrencyState] = useState<string>(() => {
    try {
      const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
      return stored || baseCurrency;
    } catch (error) {
      console.warn('Failed to load selected currency from session storage:', error);
      return baseCurrency;
    }
  });

  // Track previous currency for error recovery
  const [previousCurrency, setPreviousCurrency] = useState<string>(selectedCurrency);

  // Fetch exchange rates for the selected currency
  const {
    rates,
    isLoading: isLoadingRates,
    error: ratesError,
    convert,
  } = useExchangeRates(selectedCurrency);

  // Initialize account color manager
  const accountColorManager = useMemo(() => {
    return new AccountColorManager(accounts);
  }, [accounts]);

  // Get account colors map
  const accountColors = useMemo(() => {
    return accountColorManager.getAllColors();
  }, [accountColorManager]);

  // Persist selected currency to session storage
  useEffect(() => {
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, selectedCurrency);
    } catch (error) {
      console.warn('Failed to save selected currency to session storage:', error);
    }
  }, [selectedCurrency]);

  // Handle currency selection with error recovery
  const setSelectedCurrency = (currency: string) => {
    setPreviousCurrency(selectedCurrency);
    setSelectedCurrencyState(currency);
  };

  // Handle conversion errors - revert to previous currency
  useEffect(() => {
    if (ratesError && selectedCurrency !== previousCurrency) {
      console.error('Currency conversion failed, reverting to previous currency:', ratesError);
      setSelectedCurrencyState(previousCurrency);
    }
  }, [ratesError, selectedCurrency, previousCurrency]);

  // Convert all account balances to selected currency
  const convertedAccounts = useMemo<AccountWithConvertedBalance[]>(() => {
    if (!rates || isLoadingRates) {
      // Return accounts with original balances during loading
      return accounts.map(account => ({
        ...account,
        convertedBalance: account.balance,
        conversionRate: 1,
      }));
    }

    try {
      return accounts.map(account => {
        // If account currency matches selected currency, no conversion needed
        if (account.currency === selectedCurrency) {
          return {
            ...account,
            convertedBalance: account.balance,
            conversionRate: 1,
          };
        }

        // Convert balance to selected currency
        try {
          const convertedBalance = convert(account.balance, account.currency, selectedCurrency);
          const conversionRate = convertedBalance / account.balance;

          return {
            ...account,
            convertedBalance,
            conversionRate,
          };
        } catch (conversionError) {
          console.error(
            `Failed to convert ${account.name} from ${account.currency} to ${selectedCurrency}:`,
            conversionError
          );
          // Fallback to original balance if conversion fails for this account
          return {
            ...account,
            convertedBalance: account.balance,
            conversionRate: 1,
          };
        }
      });
    } catch (error) {
      console.error('Error during account conversion:', error);
      // Return accounts with original balances on error
      return accounts.map(account => ({
        ...account,
        convertedBalance: account.balance,
        conversionRate: 1,
      }));
    }
  }, [accounts, selectedCurrency, rates, isLoadingRates, convert]);

  // Calculate total net worth in selected currency
  const totalNetWorth = useMemo(() => {
    return convertedAccounts.reduce((total, account) => {
      return total + account.convertedBalance;
    }, 0);
  }, [convertedAccounts]);

  // Determine if conversion is in progress
  const isConverting = isLoadingRates;

  // Determine conversion error state
  const conversionError = ratesError;

  return {
    selectedCurrency,
    setSelectedCurrency,
    convertedAccounts,
    totalNetWorth,
    isConverting,
    conversionError,
    accountColors,
  };
}
